/**
 * Type definitions for Address Search Field
 * Centralized type definitions to avoid duplication
 */

/**
 * Place prediction from address search API
 */
export interface PlacePrediction {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

/**
 * Parsed address components
 */
export interface AddressComponents {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

/**
 * Supported country options for Business Search–related fields.
 * Single source of truth for Business Search MAC models and the CountryCode type.
 */
export const SUPPORTED_COUNTRY_OPTIONS = [
  { codename: "ar", name: "Argentina" },
  { codename: "at", name: "Austria" },
  { codename: "au", name: "Australia" },
  { codename: "be", name: "Belgium" },
  { codename: "bg", name: "Bulgaria" },
  { codename: "br", name: "Brazil" },
  { codename: "ca", name: "Canada" },
  { codename: "cl", name: "Chile" },
  { codename: "cn", name: "China" },
  { codename: "co", name: "Colombia" },
  { codename: "cy", name: "Cyprus" },
  { codename: "cz", name: "Czech Republic" },
  { codename: "de", name: "Germany" },
  { codename: "dk", name: "Denmark" },
  { codename: "ee", name: "Estonia" },
  { codename: "es", name: "Spain" },
  { codename: "fi", name: "Finland" },
  { codename: "fr", name: "France" },
  { codename: "gb", name: "United Kingdom" },
  { codename: "gr", name: "Greece" },
  { codename: "hr", name: "Croatia" },
  { codename: "hu", name: "Hungary" },
  { codename: "id", name: "Indonesia" },
  { codename: "ie", name: "Ireland" },
  { codename: "in", name: "India" },
  { codename: "is", name: "Iceland" },
  { codename: "it", name: "Italy" },
  { codename: "jp", name: "Japan" },
  { codename: "kr", name: "South Korea" },
  { codename: "lt", name: "Lithuania" },
  { codename: "lu", name: "Luxembourg" },
  { codename: "lv", name: "Latvia" },
  { codename: "mt", name: "Malta" },
  { codename: "mx", name: "Mexico" },
  { codename: "my", name: "Malaysia" },
  { codename: "nl", name: "Netherlands" },
  { codename: "no", name: "Norway" },
  { codename: "nz", name: "New Zealand" },
  { codename: "pl", name: "Poland" },
  { codename: "pt", name: "Portugal" },
  { codename: "ro", name: "Romania" },
  { codename: "ru", name: "Russia" },
  { codename: "se", name: "Sweden" },
  { codename: "si", name: "Slovenia" },
  { codename: "sk", name: "Slovakia" },
  { codename: "th", name: "Thailand" },
  { codename: "tr", name: "Turkey" },
  { codename: "ua", name: "Ukraine" },
  { codename: "us", name: "United States" },
  { codename: "vn", name: "Vietnam" },
  { codename: "za", name: "South Africa" },
] as const;

/**
 * Country code type — derived from SUPPORTED_COUNTRY_OPTIONS to stay in sync.
 */
export type CountryCode =
  Uppercase<(typeof SUPPORTED_COUNTRY_OPTIONS)[number]["codename"]> | "other";

/**
 * Normalises a country value (e.g. a lowercase alpha-2 code such as "mx"
 * returned by COM-FIND's location.countryCode) to an uppercase ISO 3166-1
 * alpha-2 code suitable for storing/submitting as the country field value.
 */
export function normalizeCountryCode(value: string | null | undefined): string {
  return (value ?? "").trim().toUpperCase();
}

/**
 * Converts a configured country (e.g. the `country` prop on AddressField /
 * AddressSearchField / BusinessSearchField) into its submitted form value:
 * uppercase ISO 3166-1 alpha-2, except the "other"/"international" sentinel,
 * which is preserved as-is since it isn't a real ISO code.
 */
export function toCountryFieldValue(country: string): string {
  return country.toLowerCase() === "other" ? "other" : country.toUpperCase();
}

/**
 * Address search mode
 */
export type AddressSearchMode = "search" | "manual" | "selected";

/**
 * Entry method for tracking how address was entered
 */
export type EntryMethod = "manual" | "automatic";
